import { defineStore } from 'pinia'
import { login, getPermissionInfo, logout as logoutApi } from '@/api/modules/sys/sys-user.js'
import { clearAllCanvasSnapshots } from '@/utils/canvas-snapshot.js'
import router from '@/router'

export const useUserStore = defineStore('user', {
  state: () => ({
    token: localStorage.getItem('token') || '',
    userInfo: JSON.parse(localStorage.getItem('userInfo') || '{}'),
    roles: [],
    permissions: [],
    menus: [] // 存储用户可访问的菜单信息
  }),

  getters: {
    isLogin: (state) => !!state.token,
    username: (state) => state.userInfo?.username || '',
    nickname: (state) => state.userInfo?.nickname || state.userInfo?.username || '',
    userId: (state) => state.userInfo?.id,
    feishuUserId: (state) => state.userInfo?.feishuUserId || ''
  },

  actions: {
    // 设置token
    setToken(token) {
      this.token = token
      localStorage.setItem('token', token)
    },

    // 设置用户信息
    setUserInfo(info) {
      this.userInfo = info
      localStorage.setItem('userInfo', JSON.stringify(info))
    },

    // 设置角色和权限
    setRolesAndPermissions({ roles, permissions }) {
      this.roles = roles || []
      this.permissions = permissions || []
    },

    // 登录
    async login(loginForm, options = {}) {
      // 记录登录前的用户：换了账号登录时需清上一账号的画板本地快照（隐私）
      const previousUserId = this.userInfo?.id
      try {
        const loginResponse = await login(loginForm)
        const loginData = loginResponse.data || loginResponse // 兼容不同返回格式

        // 新会话开始：清掉上一会话可能残留的登录来源标记
        // （SSO 登录后未登出、会话过期又改用密码登录时，登出不应再跳 MaxKey 统一登出）
        sessionStorage.removeItem('login_provider')
        this.setToken(loginData.accessToken)

        // 仅设置基础用户ID信息，完整信息将在getPermissionInfo中获取
        this.setUserInfo({
          id: loginData.userId,
        })

        // 获取完整的用户权限信息（包含用户信息、角色、权限、菜单等）
        await this.getPermissionInfo()

        // 换账号登录：清除所有用户的画板本地快照（含上一账号的）
        if (previousUserId && previousUserId !== loginData.userId) {
          clearAllCanvasSnapshots()
        }

        // 获取重定向地址
        const redirectQuery = options.redirect || router.currentRoute.value.query.redirect
        const redirect = Array.isArray(redirectQuery) ? redirectQuery[0] : redirectQuery
        const redirectPath = typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/'
        await router.push(redirectPath)

        return loginData
      } catch (error) {
        // 登录失败时清除状态
        this.resetState()
        throw error
      }
    },

    // 获取权限信息（包含用户信息、角色、权限、菜单等）
    async getPermissionInfo() {
      try {
        const response = await getPermissionInfo()
        const data = response.data || response // 兼容不同返回格式

        this.setUserInfo(data.user || {})

        this.setRolesAndPermissions({
          roles: data.roles || [],
          permissions: data.permissions || []
        })

        // 如果后端返回了动态路由，可以在这里处理
        if (data.menus) {
          // 可以将菜单信息存储到store中，用于后续的动态路由处理
          this.menus = data.menus
        }

        return data
      } catch (error) {
        // 获取权限信息失败时清除状态
        this.resetState()
        throw error
      }
    },

    // 登出
    async logout({ notifyServer = true } = {}) {
      if (notifyServer && this.token) {
        try {
          await logoutApi()
        } catch (error) {
          console.error('后端登出失败:', error)
        }
      }
      const loginProvider = sessionStorage.getItem('login_provider')
      sessionStorage.removeItem('login_provider')
      this.resetState()
      // 清除画板本地草稿快照（隐私：登出后不应残留可恢复的编辑内容）。
      // 放在 logout 而非 resetState——resetState 还会被登录失败/权限接口
      // 瞬时失败复用，那些场景用户并未离开，清快照会丢未保存的编辑
      clearAllCanvasSnapshots()
      // 拉森 SSO 登录的用户：本地会话清理后再结束 MaxKey 会话（统一登出）。
      // redirect_uri 回调地址需在 MaxKey 管理端加入白名单
      if (loginProvider === 'lasen' && notifyServer) {
        window.location.href = `https://sso.lasenyun.com/sign/force/logout?redirect_uri=${encodeURIComponent(window.location.origin + '/login')}`
        return
      }
      await router.push('/login')
    },

    // 重置状态
    resetState() {
      this.token = ''
      this.userInfo = {}
      this.roles = []
      this.permissions = []
      this.menus = []
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
    }
  }
}) 
